import { Navigate, useLocation, useParams } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { Spinner } from "@/components/ui/Spinner";

interface BusinessRouteProps {
  children: React.ReactNode;
  /** Required roles to access this route. Defaults to ["admin", "manager"] */
  allowedRoles?: string[];
}

/**
 * Route wrapper that requires:
 * 1. Authentication
 * 2. Membership in the specific business from URL params
 * 3. Appropriate role (admin/manager by default)
 *
 * @param props - Component props
 * @param props.children - Child components to render when authorized
 * @param props.allowedRoles - Roles allowed to access (defaults to admin/manager)
 * @returns The protected content, redirect to login, or redirect to account
 */
export function BusinessRoute({
  children,
  allowedRoles = ["admin", "manager"],
}: BusinessRouteProps) {
  const { isAuthenticated, isLoading, memberships } = useAuthStore();
  const location = useLocation();
  const { companyId } = useParams<{ companyId: string }>();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check authentication first
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Find membership for this business
  const membership = memberships.find(
    (m) => m.companyId === companyId && m.isActive
  );

  // No membership in this business - redirect to account page
  if (!membership) {
    return <Navigate to="/account" replace />;
  }

  // Check if user has an allowed role
  const hasAllowedRole = membership.roles.some((role) =>
    allowedRoles.includes(role)
  );

  if (!hasAllowedRole) {
    // User is member but doesn't have required role (e.g., driver-only)
    // Redirect to their appropriate page
    if (membership.roles.includes("driver")) {
      return <Navigate to="/account/rides" replace />;
    }
    return <Navigate to="/account" replace />;
  }

  return <>{children}</>;
}
